import React, { useState, useMemo } from "react";
import { createRoot } from "react-dom/client";
import { 
  Upload, LayoutDashboard, CalendarDays, BrainCircuit, Users 
} from "lucide-react";
import * as XLSX from "xlsx";

// 引用本地资源
import { RawRow, ScheduleItem, ParseLog } from "./src/types";
import { toLocalMidnight, parseExcelDate } from "./src/utils";

// 引用组件
import UploadView from "./src/components/UploadView";
import GanttView from "./src/components/GanttView";
import PlanningView from "./src/components/PlanningView";
import DecisionView from "./src/components/DecisionView";

const App = () => {
  const [activeTab, setActiveTab] = useState<"upload" | "gantt" | "planning" | "decision">("upload");
  const [scheduleData, setScheduleData] = useState<ScheduleItem[]>([]);
  const [predictedData, setPredictedData] = useState<ScheduleItem[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [parseLogs, setParseLogs] = useState<ParseLog[]>([]);

  const processFile = async (file: File) => {
    setIsProcessing(true);
    setParseLogs([]);
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = e.target?.result;
        const workbook = XLSX.read(data, { type: "array", cellDates: true });
        const newItems: ScheduleItem[] = [];
        const logs: ParseLog[] = [];
        const today = new Date();
        const cutoffDate = toLocalMidnight(new Date(today));
        cutoffDate.setDate(cutoffDate.getDate() - 45);

        const getCol = (row: RawRow, candidates: string[]) => {
           for (const c of candidates) {
             if (row[c] !== undefined) return row[c];
             const foundKey = Object.keys(row).find(k => k.trim() === c || k.replace(/\s/g,'') === c);
             if (foundKey) return row[foundKey];
           }
           for (const c of candidates) {
              const foundKey = Object.keys(row).find(k => k.includes(c));
              if (foundKey) return row[foundKey];
           }
           return null;
        };

        const DATE_COLS = ['预计承接日期', '承接开始日期', '承接日期', '开始日期', '排班日期', '日期', '预计承接时间', '开始时间', '时间'];
        const TEACHER_COLS = ['承接老师', '老师', '班主任', '姓名', '排班老师', '负责人', 'Teacher'];
        const GROUP_COLS = ['群号', '群名称', '群名', '班级', '群ID'];
        const COUNT_COLS = ['对应好友数量', '好友数量标准', '对应线索', '好友数量', '人数', '加粉数', '进群人数'];

        workbook.SheetNames.forEach(sheetName => {
           const sheet = workbook.Sheets[sheetName];
           const rows = XLSX.utils.sheet_to_json<RawRow>(sheet);
           const nName = sheetName.replace(/\s/g, '').toLowerCase(); 
           if (rows.length === 0) {
             logs.push({ sheetName, status: 'skipped', message: '空表', count: 0 });
             return;
           }
           let type: '21Day' | '15Day' | 'Referral' | 'Xianyu' | null = null;
           if (nName.includes('闲鱼')) type = 'Xianyu';
           else if (nName.includes('转介绍') || nName.includes('星选')) type = 'Referral';
           else if (nName.includes('21')) type = '21Day';
           else if (nName.includes('15') || nName.includes('中文')) type = '15Day';

           if (!type) {
             logs.push({ sheetName, status: 'skipped', message: '未匹配到已知排班类型', count: 0 });
             return; 
           }

           let validRows = 0;
           rows.forEach((row, idx) => {
             const startDateVal = getCol(row, DATE_COLS);
             const teacherVal = getCol(row, TEACHER_COLS);
             const groupVal = getCol(row, GROUP_COLS);
             const countVal = getCol(row, COUNT_COLS);
             const startDate = parseExcelDate(startDateVal);
             const teacher = String(teacherVal || '').trim();
             
             if (startDate && teacher && teacher.toLowerCase() !== 'undefined' && teacher !== 'null') {
               if (startDate < cutoffDate) return;
               const endDate = new Date(startDate);
               
               if (type === '21Day') endDate.setDate(startDate.getDate() + 26);
               else endDate.setDate(startDate.getDate() + 20);

               newItems.push({
                 id: `${type}-${sheetName}-${idx}`,
                 teacher,
                 groupCode: groupVal || `未知群-${idx}`,
                 startDate,
                 endDate,
                 type: type!,
                 sourceSheet: sheetName,
                 studentCount: countVal || '-',
                 raw: row,
                 isPredicted: false
               });
               validRows++;
             }
           });
           if (validRows > 0) logs.push({ sheetName, status: 'success', message: `识别为 [${type}]`, count: validRows });
           else logs.push({ sheetName, status: 'error', message: `无有效数据或数据过旧`, count: 0 });
        });
        setParseLogs(logs);
        if (newItems.length > 0) {
           setScheduleData(newItems);
           setPredictedData([]); 
        }
      } catch (err) {
        console.error(err);
        setParseLogs([{ sheetName: 'FILE ERROR', status: 'error', message: '文件解析彻底失败', count: 0 }]);
      } finally {
        setIsProcessing(false);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleApplyPredictions = (items: ScheduleItem[]) => {
     setPredictedData(items);
     setActiveTab("gantt");
  };

  const displayData = useMemo(() => [...scheduleData, ...predictedData], [scheduleData, predictedData]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex flex-col">
      <header className="backdrop-blur-xl bg-gradient-to-r from-slate-900/95 via-blue-900/95 to-indigo-900/95 text-white p-4 shadow-2xl sticky top-0 z-50 border-b border-white/10">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3 font-bold text-xl">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center shadow-lg">
              <Users className="text-white" size={20} />
            </div>
            <div className="flex flex-col">
              <span className="bg-gradient-to-r from-white to-blue-200 bg-clip-text text-transparent">智能排班系统 v2.0</span>
              <span className="text-xs font-normal text-blue-300/70">模块化架构版</span>
            </div>
          </div>
          <nav className="flex gap-2">
            <button 
              onClick={() => setActiveTab("upload")} 
              className={`group relative px-4 py-2.5 rounded-xl transition-all duration-300 flex items-center gap-2 font-medium ${activeTab === 'upload' ? 'bg-white/20 shadow-lg shadow-blue-500/20 scale-105' : 'hover:bg-white/10 hover:scale-105'}`}
            >
              <Upload size={16} className={`transition-transform duration-300 ${activeTab === 'upload' ? 'scale-110' : 'group-hover:scale-110'}`} /> 
              <span className="text-sm">数据上传</span>
              {activeTab === 'upload' && <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-blue-500/20 to-indigo-500/20 blur-xl"></div>}
            </button>
            <button 
              onClick={() => scheduleData.length > 0 && setActiveTab("gantt")} 
              disabled={scheduleData.length === 0} 
              className={`group relative px-4 py-2.5 rounded-xl transition-all duration-300 flex items-center gap-2 font-medium ${activeTab === 'gantt' ? 'bg-white/20 shadow-lg shadow-blue-500/20 scale-105' : 'hover:bg-white/10 hover:scale-105 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100'}`}
            >
              <LayoutDashboard size={16} className={`transition-transform duration-300 ${activeTab === 'gantt' ? 'scale-110' : 'group-hover:scale-110'}`} /> 
              <span className="text-sm">排班甘特图</span>
              {displayData.length > 0 && <span className="ml-1 px-1.5 py-0.5 bg-blue-400/30 rounded-full text-xs font-bold">{displayData.length}</span>}
              {activeTab === 'gantt' && <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-blue-500/20 to-indigo-500/20 blur-xl"></div>}
            </button>
            <button 
              onClick={() => scheduleData.length > 0 && setActiveTab("planning")} 
              disabled={scheduleData.length === 0} 
              className={`group relative px-4 py-2.5 rounded-xl transition-all duration-300 flex items-center gap-2 font-medium ${activeTab === 'planning' ? 'bg-white/20 shadow-lg shadow-blue-500/20 scale-105' : 'hover:bg-white/10 hover:scale-105 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100'}`}
            >
              <CalendarDays size={16} className={`transition-transform duration-300 ${activeTab === 'planning' ? 'scale-110' : 'group-hover:scale-110'}`} /> 
              <span className="text-sm">智能排期</span>
              {activeTab === 'planning' && <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-blue-500/20 to-indigo-500/20 blur-xl"></div>}
            </button>
            <button 
              onClick={() => scheduleData.length > 0 && setActiveTab("decision")} 
              disabled={scheduleData.length === 0} 
              className={`group relative px-4 py-2.5 rounded-xl transition-all duration-300 flex items-center gap-2 font-medium ${activeTab === 'decision' ? 'bg-white/20 shadow-lg shadow-blue-500/20 scale-105' : 'hover:bg-white/10 hover:scale-105 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100'}`}
            >
              <BrainCircuit size={16} className={`transition-transform duration-300 ${activeTab === 'decision' ? 'scale-110' : 'group-hover:scale-110'}`} /> 
              <span className="text-sm">预排决策</span>
              {activeTab === 'decision' && <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-blue-500/20 to-indigo-500/20 blur-xl"></div>}
            </button>
          </nav>
        </div>
      </header>
      <main className="flex-1 p-4 md:p-8 max-w-[1600px] mx-auto w-full">
        {activeTab === "upload" && (
          <UploadView 
            onUpload={processFile} 
            isProcessing={isProcessing} 
            logs={parseLogs} 
            hasData={scheduleData.length > 0} 
            onViewChart={() => setActiveTab("gantt")} 
          />
        )}
        {activeTab === "gantt" && <GanttView data={displayData} />}
        {activeTab === "planning" && <PlanningView data={displayData} />}
        {activeTab === "decision" && <DecisionView data={scheduleData} onGenerate={handleApplyPredictions} />}
      </main>
    </div>
  );
};

const root = createRoot(document.getElementById("root")!);
root.render(<App />);